import { useEffect, useRef, useState } from "react";
import {
  buildHostMetricsWebSocketUrl,
  type HostMetricsMessage,
} from "../lib/hostMetricsWs";

export type HostWsStatus = "idle" | "connecting" | "open" | "closed" | "error";

/** 最近若干帧，供趋势图与 KPI 旁微型折线 */
export interface HostMetricsSeriesPoint {
  ts: string;
  cpu: number;
  mem: number;
  disk: number | null;
  swap: number;
  /** Unix 1 分钟负载；Windows 常为 null */
  load1: number | null;
}

const SERIES_CAP = 48;

export function useAdminHostMetricsWs(enabled: boolean) {
  const [status, setStatus] = useState<HostWsStatus>("idle");
  const [metrics, setMetrics] = useState<HostMetricsMessage | null>(null);
  const [series, setSeries] = useState<HostMetricsSeriesPoint[]>([]);
  const [lastError, setLastError] = useState<string | null>(null);
  const wsRef = useRef<WebSocket | null>(null);

  useEffect(() => {
    if (!enabled) {
      setStatus("idle");
      setSeries([]);
      return;
    }
    const url = buildHostMetricsWebSocketUrl();
    if (!url) {
      setStatus("closed");
      setLastError("未登录");
      return;
    }

    let closed = false;
    let ws: WebSocket | null = null;

    const connect = () => {
      if (closed) return;
      setStatus("connecting");
      setLastError(null);
      ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        if (closed) return;
        setStatus("open");
      };
      ws.onmessage = (ev) => {
        try {
          const j = JSON.parse(ev.data as string) as HostMetricsMessage;
          if (j && j.host && j.memory) {
            setMetrics(j);
            setSeries((prev) => {
              const pt: HostMetricsSeriesPoint = {
                ts: j.ts,
                cpu: j.host.cpu_percent,
                mem: j.memory.percent,
                disk: j.disk?.percent ?? null,
                swap: j.swap.percent,
                load1:
                  j.host.load_avg != null && j.host.load_avg.length > 0
                    ? j.host.load_avg[0]
                    : null,
              };
              return [...prev, pt].slice(-SERIES_CAP);
            });
          }
        } catch {
          /* ignore */
        }
      };
      ws.onerror = () => {
        if (closed) return;
        setLastError("连接异常");
        setStatus("error");
      };
      ws.onclose = (ev) => {
        if (closed) return;
        wsRef.current = null;
        setStatus("closed");
        if (ev.code === 1008) {
          setLastError(ev.reason || "无权限或 token 无效");
          return;
        }
        window.setTimeout(() => {
          if (!closed) connect();
        }, 3000);
      };
    };

    connect();

    return () => {
      closed = true;
      ws?.close();
      wsRef.current = null;
    };
  }, [enabled]);

  return { status, metrics, series, lastError };
}
